/**
 * Vertex-stitch merge: build the merged room's floor polygon directly from the
 * member rooms' own floor-polygon vertices, instead of re-tracing structure
 * walls.
 *
 * Why: re-tracing a final structure is not idempotent — it drifts small
 * features by ~half a wall thickness and can drop thin notches. The member
 * floor polygons are already the exact, rectilinear final boundaries, so the
 * merged boundary is best built from THEM: walk each room's boundary, and where
 * a demolished partition runs along an edge, bridge across the wall to the other
 * room instead of following the (now-gone) partition face.
 *
 * Algorithm (directed half-edge stitch):
 *   1. For every polygon edge of every member room, compute the sub-intervals
 *      that lie on a demolished partition span (`demolishedIntervals`). Keep the
 *      non-demolished sub-edges as directed boundary segments. At each demolished
 *      sub-interval, emit an OUT-port (where the boundary leaves this room) and
 *      an IN-port (where it would re-enter), tagged with the spine + axis
 *      position.
 *   2. Bridge each OUT-port to the IN-port of a DIFFERENT room on the SAME spine
 *      at the nearest axis position — across a wall the two are anti-parallel and
 *      sit at the same axis coordinate, so this pairs them exactly even with
 *      multiple partitions.
 *   3. Chain sub-edges + bridges into closed loops; return the largest-area loop
 *      (the merged outer boundary).
 *
 * Pure. Coordinates are XZ (floor plane). Member polygons must share consistent
 * winding (the pipeline emits CCW floor polygons).
 */

const { axisOf } = require('./segments');

const PARALLEL_DOT = 0.985; // edge ∥ spine when |dir·spineDir| ≥ this
const FACE_PERP_PAD_M = 0.06; // edge counts as on the wall within half-thick + this
const MIN_DEMOLISHED_M = 0.02; // ignore demolished slivers shorter than this
const MAX_BRIDGE_M = 0.5; // reject a port bridge longer than this (no real partner)
const KEY_SCALE = 1000; // node key quantization (1 mm)

function lerp(a, b, t) {
  return { x: a.x + (b.x - a.x) * t, z: a.z + (b.z - a.z) * t };
}
function nodeKey(p) {
  return `${Math.round(p.x * KEY_SCALE)},${Math.round(p.z * KEY_SCALE)}`;
}
function signedArea(poly) {
  let s = 0;
  for (let i = 0; i < poly.length; i++) {
    const a = poly[i];
    const b = poly[(i + 1) % poly.length];
    s += a.x * b.z - b.x * a.z;
  }
  return s / 2;
}

/** Spine descriptors (axis + span + half-thickness) for the demolished segments. */
function spinesFor(segments, structure) {
  return segments
    .map((seg) => {
      const sw = (structure.walls || []).find(
        (w) => w.id === seg.structureWallId
      );
      if (!sw) return null;
      return {
        ax: axisOf(sw),
        span: seg.span,
        half: (sw.dimensions?.z || 0.114) / 2,
      };
    })
    .filter(Boolean);
}

/**
 * Sub-intervals of edge p0→p1 (as { t0, t1, spineIdx }, sorted by t0) that lie
 * on a demolished partition span: edge parallel to the spine, within
 * half-thickness of its centerline, and overlapping the demolished span.
 */
function demolishedIntervals(p0, p1, spines) {
  const dx = p1.x - p0.x;
  const dz = p1.z - p0.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-9) return [];
  const ex = dx / len;
  const ez = dz / len;
  const mx = (p0.x + p1.x) / 2;
  const mz = (p0.z + p1.z) / 2;
  const out = [];
  spines.forEach((sp, spineIdx) => {
    const { ax, span, half } = sp;
    if (Math.abs(ex * ax.dx + ez * ax.dz) < PARALLEL_DOT) return; // not parallel
    if (
      Math.abs((mx - ax.ox) * -ax.dz + (mz - ax.oz) * ax.dx) >
      half + FACE_PERP_PAD_M
    ) {
      return; // not on this wall's face
    }
    const e0 = (p0.x - ax.ox) * ax.dx + (p0.z - ax.oz) * ax.dz;
    const e1 = (p1.x - ax.ox) * ax.dx + (p1.z - ax.oz) * ax.dz;
    const lo = Math.max(Math.min(e0, e1), span.min);
    const hi = Math.min(Math.max(e0, e1), span.max);
    if (hi - lo <= MIN_DEMOLISHED_M) return;
    let t0 = (lo - e0) / (e1 - e0);
    let t1 = (hi - e0) / (e1 - e0);
    if (t0 > t1) [t0, t1] = [t1, t0];
    out.push({ t0: Math.max(0, t0), t1: Math.min(1, t1), spineIdx });
  });
  return out.sort((a, b) => a.t0 - b.t0);
}

/** Axis coordinate of a point on a spine. */
function axisT(p, ax) {
  return (p.x - ax.ox) * ax.dx + (p.z - ax.oz) * ax.dz;
}

/**
 * Stitch the member room polygons into the merged boundary polygon.
 *
 * @param {{x,z}[][]} polys  member room floor polygons (≥2), consistent winding
 * @param {{structureWallId, span}[]} segments  demolished partition segments
 * @param {{walls:object[]}} structure  for spine geometry lookup
 * @returns {{ polygon: {x,z}[] } | { error: string }}
 */
function stitchMergedPolygon(polys, segments, structure) {
  const spines = spinesFor(segments, structure);
  if (spines.length === 0) return { error: 'no spine geometry' };

  const subEdges = []; // { a, b }  surviving boundary segments
  const outPorts = []; // { p, room, spineIdx, t }
  const inPorts = []; // { p, room, spineIdx, t }

  for (let ri = 0; ri < polys.length; ri++) {
    const P = polys[ri];
    for (let i = 0; i < P.length; i++) {
      const p0 = P[i];
      const p1 = P[(i + 1) % P.length];
      const dem = demolishedIntervals(p0, p1, spines);
      if (dem.length === 0) {
        subEdges.push({ a: p0, b: p1 });
        continue;
      }
      let cursor = 0;
      for (const { t0, t1, spineIdx } of dem) {
        if (t0 > cursor + 1e-6)
          subEdges.push({ a: lerp(p0, p1, cursor), b: lerp(p0, p1, t0) });
        const ax = spines[spineIdx].ax;
        const op = lerp(p0, p1, t0);
        const ip = lerp(p0, p1, t1);
        outPorts.push({ p: op, room: ri, spineIdx, t: axisT(op, ax) });
        inPorts.push({ p: ip, room: ri, spineIdx, t: axisT(ip, ax) });
        cursor = t1;
      }
      if (cursor < 1 - 1e-6) subEdges.push({ a: lerp(p0, p1, cursor), b: p1 });
    }
  }
  if (outPorts.length === 0)
    return { error: 'no demolished edges on member boundaries' };

  // Bridge each OUT-port across the wall: the IN-port of a different room on the
  // same spine at the nearest axis position (anti-parallel → same coordinate).
  const bridges = [];
  for (const op of outPorts) {
    let best = Infinity;
    let target = null;
    for (const ip of inPorts) {
      if (ip.room === op.room || ip.spineIdx !== op.spineIdx) continue;
      const dt = Math.abs(ip.t - op.t);
      if (dt < best) {
        best = dt;
        target = ip;
      }
    }
    if (
      !target ||
      Math.hypot(op.p.x - target.p.x, op.p.z - target.p.z) > MAX_BRIDGE_M
    ) {
      return { error: 'unpaired demolished port (no partner across the wall)' };
    }
    bridges.push({ a: op.p, b: target.p, isBridge: true });
  }

  // Chain into loops; keep the largest-area one.
  const all = [...subEdges, ...bridges];
  const byStart = new Map();
  for (const e of all) {
    const k = nodeKey(e.a);
    if (!byStart.has(k)) byStart.set(k, []);
    byStart.get(k).push(e);
  }
  const used = new Set();
  let bestPoly = null;
  let bestArea = 0;
  for (const seed of all) {
    if (used.has(seed)) continue;
    const loop = [];
    let e = seed;
    let steps = 0;
    const seedKey = nodeKey(seed.a);
    while (e && steps++ < all.length + 5) {
      used.add(e);
      loop.push(e.a);
      const nk = nodeKey(e.b);
      if (nk === seedKey) break;
      const cands = byStart.get(nk);
      e = cands ? cands.find((c) => !used.has(c)) || null : null;
    }
    if (loop.length >= 3) {
      const ar = Math.abs(signedArea(loop));
      if (ar > bestArea) {
        bestArea = ar;
        bestPoly = loop;
      }
    }
  }
  if (!bestPoly) return { error: 'stitch did not close a loop' };
  return { polygon: bestPoly };
}

const MIN_SURVIVOR_M = 0.1; // drop a clipped survivor shorter than this

function lerp3(a, b, t) {
  return {
    x: a.x + (b.x - a.x) * t,
    y: (a.y ?? 0) + ((b.y ?? 0) - (a.y ?? 0)) * t,
    z: a.z + (b.z - a.z) * t,
  };
}

/**
 * Clip a polygon (3D corners) to the slab uLo ≤ u ≤ uHi, where
 * u = projection onto `dir` measured from `origin` in the XZ plane. Two
 * Sutherland-Hodgman half-space clips; handles sloped wall tops, not just
 * rectangles.
 */
function clipPolygonByAxis(corners, origin, dir, uLo, uHi) {
  const u = (c) => (c.x - origin.x) * dir.x + (c.z - origin.z) * dir.z;
  const half = (poly, keepGreater, bound) => {
    const out = [];
    for (let i = 0; i < poly.length; i++) {
      const a = poly[i];
      const b = poly[(i + 1) % poly.length];
      const ua = u(a);
      const ub = u(b);
      const ina = keepGreater ? ua >= bound : ua <= bound;
      const inb = keepGreater ? ub >= bound : ub <= bound;
      if (ina) out.push(a);
      if (ina !== inb) out.push(lerp3(a, b, (bound - ua) / (ub - ua)));
    }
    return out;
  };
  return half(half(corners, true, uLo), false, uHi);
}

/** Deep clone of a wall record. Used wherever a member wall is carried into the
 *  merged set, so the in-place junction-cleanup passes (coverSlivers etc.) never
 *  reach back and mutate the caller's input rooms. */
const cloneWall = (w) => JSON.parse(JSON.stringify(w));

/** A member wall clipped to the sub-fraction [s0,s1] of its bottomLeft→bottomRight. */
function clipWall(w, s0, s1, newId) {
  const bl = w.alignment.bottomLeft;
  const br = w.alignment.bottomRight;
  const fullLen = Math.hypot(br.x - bl.x, br.z - bl.z) || 1;
  const dir = { x: (br.x - bl.x) / fullLen, z: (br.z - bl.z) / fullLen };
  const nbl = lerp3(bl, br, s0);
  const nbr = lerp3(bl, br, s1);
  const out = JSON.parse(JSON.stringify(w));
  out.id = newId;
  out.alignment.bottomLeft = nbl;
  out.alignment.bottomRight = nbr;
  out.dimensions = {
    ...w.dimensions,
    x: Math.hypot(nbr.x - nbl.x, nbr.z - nbl.z),
  };
  out.center = {
    x: (nbl.x + nbr.x) / 2,
    y: w.center?.y ?? 0,
    z: (nbl.z + nbr.z) / 2,
  };
  if (Array.isArray(w.polygonCorners)) {
    out.polygonCorners = clipPolygonByAxis(
      w.polygonCorners,
      bl,
      dir,
      s0 * fullLen,
      s1 * fullLen
    );
  }
  return out;
}

const COLLINEAR_DIR_DOT = 0.99; // wall faces parallel when |dir·dir| ≥ this
const COLLINEAR_PERP_M = 0.06; // ...and coincident within this perpendicular gap
const MAX_JUNCTION_GAP_M = 0.2; // merge collinear walls separated by ≤ this gap

/** A wall's face line (unit dir + length) from its alignment corners, or null. */
function faceLine(w) {
  const bl = w.alignment?.bottomLeft;
  const br = w.alignment?.bottomRight;
  if (!bl || !br) return null;
  const dx = br.x - bl.x;
  const dz = br.z - bl.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-9) return null;
  return { dir: { x: dx / len, z: dz / len }, len };
}

/**
 * Merge a cluster of collinear, same-face walls into one spanning their union.
 * Keeps the LONGEST member's id/texture/category/height; the gap between them
 * (the demolished partition's footprint where it tee'd into this wall) is
 * absorbed, so a partition junction yields one continuous wall, not two with a
 * wall-thickness sliver between. polygonCorners are rebuilt as a flat quad over
 * the union (junction walls are flat-topped).
 */
function mergeWallCluster(cluster) {
  if (cluster.length === 1) return cluster[0];
  const base = cluster
    .slice()
    .sort((a, b) => (b.dimensions?.x || 0) - (a.dimensions?.x || 0))[0];
  const fl = faceLine(base);
  const o = base.alignment.bottomLeft;
  let tMin = Infinity;
  let tMax = -Infinity;
  for (const w of cluster) {
    for (const p of [w.alignment.bottomLeft, w.alignment.bottomRight]) {
      const t = (p.x - o.x) * fl.dir.x + (p.z - o.z) * fl.dir.z;
      if (t < tMin) tMin = t;
      if (t > tMax) tMax = t;
    }
  }
  const yFloor = base.alignment.bottomLeft.y ?? 0;
  const bl = { x: o.x + fl.dir.x * tMin, y: yFloor, z: o.z + fl.dir.z * tMin };
  const br = { x: o.x + fl.dir.x * tMax, y: yFloor, z: o.z + fl.dir.z * tMax };
  const out = JSON.parse(JSON.stringify(base));
  out.alignment.bottomLeft = bl;
  out.alignment.bottomRight = br;
  out.dimensions = { ...base.dimensions, x: tMax - tMin };
  out.center = {
    x: (bl.x + br.x) / 2,
    y: base.center?.y ?? 0,
    z: (bl.z + br.z) / 2,
  };
  if (Array.isArray(base.polygonCorners) && base.polygonCorners.length >= 4) {
    const ys = base.polygonCorners.map((c) => c.y);
    const yLo = Math.min(...ys);
    const yHi = Math.max(...ys);
    out.polygonCorners = [
      { x: bl.x, y: yLo, z: bl.z },
      { x: br.x, y: yLo, z: br.z },
      { x: br.x, y: yHi, z: br.z },
      { x: bl.x, y: yHi, z: bl.z },
    ];
  }
  return out;
}

/**
 * Coalesce walls that share a structureWallId and lie on the same face into a
 * single continuous wall when separated only by a small (partition-footprint)
 * gap. Both member rooms bordered the same structure wall on either side of the
 * now-demolished partition, so this stitches their two wall pieces — and the
 * sliver between — into one. Walls with no structureWallId, or genuinely
 * separated pieces, pass through unchanged.
 */
function mergeCollinearMemberWalls(walls) {
  const bySid = new Map();
  const out = [];
  for (const w of walls) {
    const sid = w.alignment?.structureWallId;
    if (!sid) {
      out.push(w);
      continue;
    }
    if (!bySid.has(sid)) bySid.set(sid, []);
    bySid.get(sid).push(w);
  }
  for (const group of bySid.values()) {
    if (group.length === 1) {
      out.push(group[0]);
      continue;
    }
    const clusters = [];
    for (const w of group) {
      const fl = faceLine(w);
      let placed = false;
      if (fl) {
        for (const cl of clusters) {
          const c0 = cl[0];
          const fl0 = faceLine(c0);
          if (!fl0) continue;
          if (
            Math.abs(fl.dir.x * fl0.dir.x + fl.dir.z * fl0.dir.z) <
            COLLINEAR_DIR_DOT
          ) {
            continue;
          }
          const o = c0.alignment.bottomLeft;
          const proj = (p) => (p.x - o.x) * fl0.dir.x + (p.z - o.z) * fl0.dir.z;
          const perp = (p) =>
            Math.abs((p.x - o.x) * -fl0.dir.z + (p.z - o.z) * fl0.dir.x);
          if (perp(w.alignment.bottomLeft) > COLLINEAR_PERP_M) continue;
          let clMin = Infinity;
          let clMax = -Infinity;
          for (const cw of cl) {
            for (const p of [
              cw.alignment.bottomLeft,
              cw.alignment.bottomRight,
            ]) {
              const t = proj(p);
              clMin = Math.min(clMin, t);
              clMax = Math.max(clMax, t);
            }
          }
          const w0 = proj(w.alignment.bottomLeft);
          const w1 = proj(w.alignment.bottomRight);
          const gap = Math.max(
            clMin - Math.max(w0, w1),
            Math.min(w0, w1) - clMax,
            0
          );
          if (gap <= MAX_JUNCTION_GAP_M) {
            cl.push(w);
            placed = true;
            break;
          }
        }
      }
      if (!placed) clusters.push([w]);
    }
    for (const cl of clusters) out.push(mergeWallCluster(cl));
  }
  return out;
}

const MAX_SLIVER_M = 0.2; // an uncovered boundary edge this short is a junction sliver

/** Is the segment p0→p1 covered by some wall's face (collinear, coincident, in span)? */
function edgeCoveredByWalls(p0, p1, walls) {
  const dx = p1.x - p0.x;
  const dz = p1.z - p0.z;
  const len = Math.hypot(dx, dz);
  if (len < 1e-9) return true;
  const ex = dx / len;
  const ez = dz / len;
  const mx = (p0.x + p1.x) / 2;
  const mz = (p0.z + p1.z) / 2;
  for (const w of walls) {
    const fl = faceLine(w);
    if (!fl) continue;
    if (Math.abs(ex * fl.dir.x + ez * fl.dir.z) < COLLINEAR_DIR_DOT) continue;
    const o = w.alignment.bottomLeft;
    if (
      Math.abs((mx - o.x) * -fl.dir.z + (mz - o.z) * fl.dir.x) >
      COLLINEAR_PERP_M + 0.02
    ) {
      continue;
    }
    const t = (mx - o.x) * fl.dir.x + (mz - o.z) * fl.dir.z;
    // Tight end tolerance: a verbatim wall ends at the partition's NEAR face but
    // the merged corner is at the FAR face, so a ~0.1m sliver's midpoint sits
    // just past the wall end — it must read as UNCOVERED so coverSlivers extends
    // the wall to the corner. A genuinely covered edge's midpoint is well inside.
    if (t >= -0.02 && t <= fl.len + 0.02) return true;
  }
  return false;
}

/**
 * Extend a wall over any short uncovered boundary edge that lies on its own
 * line — the wall-thickness sliver of a remaining wall left exposed where a
 * demolished partition tee'd into it, when only one room bordered that wall (so
 * `mergeCollinearMemberWalls` had nothing to coalesce). The abutting wall is
 * grown to swallow the sliver, keeping it a single continuous wall. Mutates
 * walls in place; returns the same array. `polygon` is the stitched boundary.
 */
function coverSlivers(walls, polygon) {
  if (!polygon) return walls;
  for (let i = 0; i < polygon.length; i++) {
    const p0 = polygon[i];
    const p1 = polygon[(i + 1) % polygon.length];
    const len = Math.hypot(p1.x - p0.x, p1.z - p0.z);
    if (len < 0.02 || len > MAX_SLIVER_M) continue;
    if (edgeCoveredByWalls(p0, p1, walls)) continue;
    const ex = (p1.x - p0.x) / len;
    const ez = (p1.z - p0.z) / len;
    const mx = (p0.x + p1.x) / 2;
    const mz = (p0.z + p1.z) / 2;
    // The longest wall collinear + coincident + abutting (gap ≤ sliver) this edge.
    let best = null;
    for (const w of walls) {
      const fl = faceLine(w);
      if (!fl) continue;
      if (Math.abs(ex * fl.dir.x + ez * fl.dir.z) < COLLINEAR_DIR_DOT) continue;
      const o = w.alignment.bottomLeft;
      if (
        Math.abs((mx - o.x) * -fl.dir.z + (mz - o.z) * fl.dir.x) >
        COLLINEAR_PERP_M + 0.02
      ) {
        continue;
      }
      const proj = (p) => (p.x - o.x) * fl.dir.x + (p.z - o.z) * fl.dir.z;
      const e0 = proj(p0);
      const e1 = proj(p1);
      const gap = Math.max(Math.min(e0, e1) - fl.len, 0 - Math.max(e0, e1), 0);
      if (gap > MAX_SLIVER_M) continue;
      if (!best || fl.len > faceLine(best).len) best = w;
    }
    if (!best) continue;
    const yFloor = best.alignment.bottomLeft.y ?? 0;
    const synth = {
      alignment: {
        bottomLeft: { x: p0.x, y: yFloor, z: p0.z },
        bottomRight: { x: p1.x, y: yFloor, z: p1.z },
        structureWallId: best.alignment.structureWallId,
      },
      center: best.center,
      dimensions: { ...best.dimensions, x: len },
      polygonCorners: best.polygonCorners,
    };
    Object.assign(best, mergeWallCluster([best, synth]));
  }
  return walls;
}

const COLLAPSE_SLIVER_M = 0.18; // a wall-less boundary edge this short is collapsed
// (~1.5× wall thickness — catches partition-junction notches; real openings,
// always >0.3 m, are far larger, and walled edges are protected regardless)

/** Intersection of line (p1→p2) with line (p3→p4), or null if parallel. */
function lineIntersect(p1, p2, p3, p4) {
  const d = (p1.x - p2.x) * (p3.z - p4.z) - (p1.z - p2.z) * (p3.x - p4.x);
  if (Math.abs(d) < 1e-9) return null;
  const t = ((p1.x - p3.x) * (p3.z - p4.z) - (p1.z - p3.z) * (p3.x - p4.x)) / d;
  return { x: p1.x + t * (p2.x - p1.x), z: p1.z + t * (p2.z - p1.z) };
}

/**
 * Collapse tiny boundary edges with NO wall behind them — leftover notches/steps
 * where the stitch joined two member polygons. A walled tiny edge (a real jog)
 * is kept. Each is removed by extending its two neighbour edges to their
 * intersection (a clean corner; midpoint fallback when neighbours are parallel),
 * keeping the boundary rectilinear. Pure; returns a new polygon.
 */
function collapseSlivers(polygon, walls) {
  let poly = polygon.map((p) => ({ x: p.x, z: p.z }));
  for (let guard = 0; guard < polygon.length * 3 && poly.length > 3; guard++) {
    const n = poly.length;
    let found = -1;
    for (let i = 0; i < n; i++) {
      const A = poly[i];
      const B = poly[(i + 1) % n];
      if (
        Math.hypot(B.x - A.x, B.z - A.z) < COLLAPSE_SLIVER_M &&
        !edgeCoveredByWalls(A, B, walls)
      ) {
        found = i;
        break;
      }
    }
    if (found < 0) break;
    const i = found;
    const A = poly[i];
    const B = poly[(i + 1) % n];
    const P = poly[(i - 1 + n) % n];
    const Q = poly[(i + 2) % n];
    const mid = { x: (A.x + B.x) / 2, z: (A.z + B.z) / 2 };
    let X = lineIntersect(P, A, B, Q);
    if (!X || Math.hypot(X.x - mid.x, X.z - mid.z) > 0.5) X = mid;
    const iB = (i + 1) % n;
    const np = [];
    for (let k = 0; k < n; k++) {
      if (k === i) np.push(X);
      else if (k === iB) continue;
      else np.push(poly[k]);
    }
    poly = np;
  }
  return poly;
}

const MAX_ORPHAN_SLIVER_M = 0.3; // only a fragment this short may be dropped as orphaned

/**
 * Drop SHORT walls that don't lie on the merged room's boundary. A jog one
 * member had (a wall-thickness step) can become interior once the other room
 * fills in beside it — leaving an orphaned floating fragment with no boundary
 * behind it. Such slivers are deleted; a longer off-boundary wall is a genuine
 * standing interior divider (e.g. a second shared wall that wasn't demolished)
 * and is kept. A wall is "on the boundary" if a sample of it (mid + endpoints)
 * lies on some boundary edge (collinear, coincident, in span). Pure.
 */
function dropOrphanedWalls(walls, polygon) {
  if (!polygon || polygon.length < 3) return walls;
  const onBoundary = (pt, dir) => {
    for (let i = 0; i < polygon.length; i++) {
      const p0 = polygon[i];
      const p1 = polygon[(i + 1) % polygon.length];
      const ex = p1.x - p0.x;
      const ez = p1.z - p0.z;
      const el = Math.hypot(ex, ez);
      if (el < 1e-9) continue;
      const ux = ex / el;
      const uz = ez / el;
      if (Math.abs(dir.x * ux + dir.z * uz) < COLLINEAR_DIR_DOT) continue;
      if (
        Math.abs((pt.x - p0.x) * -uz + (pt.z - p0.z) * ux) >
        COLLINEAR_PERP_M + 0.02
      ) {
        continue;
      }
      const t = (pt.x - p0.x) * ux + (pt.z - p0.z) * uz;
      if (t >= -0.05 && t <= el + 0.05) return true;
    }
    return false;
  };
  return walls.filter((w) => {
    const fl = faceLine(w);
    const bl = w.alignment?.bottomLeft;
    const br = w.alignment?.bottomRight;
    if (!fl || !bl || !br) return true; // keep walls we can't place
    if (fl.len >= MAX_ORPHAN_SLIVER_M) return true; // long → legit interior wall
    const mid = { x: (bl.x + br.x) / 2, z: (bl.z + br.z) / 2 };
    return [mid, bl, br].some((pt) => onBoundary(pt, fl.dir));
  });
}

/**
 * Derive the merged room's walls from the MEMBER rooms' own walls: a wall not
 * on a demolished partition is kept VERBATIM (same id, texture, height,
 * structureWallId — so its fixtures, locks, and textures need no remap); a wall
 * fully on the demolished overlap is dropped; a wall partly demolished is
 * clipped to its surviving extent (longest survivor keeps the original id).
 * Finally, the two pieces both rooms had on a shared wall (flanking the
 * demolished partition) are coalesced into one continuous wall.
 *
 * This is the payoff of the vertex stitch: no re-uuid, so most edits carry
 * across untouched. Pure.
 */
function deriveMergedWalls(memberRooms, segments, structure, genId) {
  const spines = spinesFor(segments, structure);
  const out = [];
  for (const room of memberRooms) {
    for (const w of room.walls || []) {
      const bl = w.alignment?.bottomLeft;
      const br = w.alignment?.bottomRight;
      if (!bl || !br) {
        out.push(cloneWall(w));
        continue;
      }
      const dem = demolishedIntervals(bl, br, spines);
      if (dem.length === 0) {
        out.push(cloneWall(w)); // not on a demolished partition — verbatim (cloned)
        continue;
      }
      // Surviving fractions = complement of the demolished intervals.
      const surviving = [];
      let cursor = 0;
      for (const { t0, t1 } of dem) {
        if (t0 > cursor + 1e-6) surviving.push([cursor, t0]);
        cursor = Math.max(cursor, t1);
      }
      if (cursor < 1 - 1e-6) surviving.push([cursor, 1]);
      if (surviving.length === 0) continue; // fully demolished — drop
      const fullLen = Math.hypot(br.x - bl.x, br.z - bl.z);
      surviving.sort((a, b) => b[1] - b[0] - (a[1] - a[0])); // longest first
      surviving.forEach(([s0, s1], k) => {
        if ((s1 - s0) * fullLen < MIN_SURVIVOR_M) return;
        out.push(clipWall(w, s0, s1, k === 0 ? w.id : genId()));
      });
    }
  }
  return mergeCollinearMemberWalls(out);
}

module.exports = {
  stitchMergedPolygon,
  deriveMergedWalls,
  mergeCollinearMemberWalls,
  coverSlivers,
  collapseSlivers,
  dropOrphanedWalls,
  clipWall,
  demolishedIntervals,
  // exposed for tuning / tests
  PARALLEL_DOT,
  FACE_PERP_PAD_M,
  MIN_DEMOLISHED_M,
  MAX_BRIDGE_M,
};
